import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Col,
  Row,
  Label,
} from "reactstrap";
import TextEditor from "components/editor/TextEditor";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate } from "react-router-dom"
import { httpRequest } from "utils/httpRequest";
import { toast } from 'react-toastify';

const EditVideo = () => {
  const [responseVideoUrl, setResponseVideoUrl] = useState(null);
  const [videoLink, setVideoLink] = useState("")
  const [imageLink, setImageLink] = useState(null)
  const [buttonLoading, setButtonLoading] = useState(false);
  const [loading, setLoading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const { id } = useParams()

  const navigate = useNavigate()

  // Validation Schema
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Video title is required")
      .min(3, "Title must be at least 3 characters"),
    description: Yup.string()
      .required("Description is required")
      .test('not-empty-html', 'Description is required', function (value) {
        if (!value) return false;
        // Remove HTML tags and check if there's actual content
        const textOnly = value.replace(/<[^>]*>/g, '').trim();
        return textOnly.length > 0;
      }),

    // video: Yup.mixed()
    //   .test("required", "A video file is required", function (value) {
    //     return value instanceof File || typeof value === 'string';
    //   })
    //   .test("fileType", "Only video files are allowed", function (value) {
    //     if (!value || typeof value === 'string') return true; // if it's a string URL or empty, don't validate type
    //     return ["video/mp4"].includes(value.type);
    //   }),

    video: Yup.string()
      .required("A video file is required")
      .test(
        "fileType",
        "Invalid youtube url",
        (value) => {
          const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
          const match = value?.match(regex);
          return match ? match[1] : null;
        }
      ),

    image: Yup.mixed()
      .test('image-check', 'Image is required', function (value) {
        // Valid if we have either a file or an existing image path
        return value !== null && value !== undefined;
      })
    // .test(
    //   "fileType",
    //   "Only image files are allowed",
    //   (value) => {
    //     // Skip validation if it's an existing image path (string)
    //     if (typeof value === 'string') return true;
    //     // Validate file type if it's a new upload
    //     return !value || ["image/jpeg", "image/png", "image/gif"].includes(value.type);
    //   }
    // )
    // .test(
    //   "fileSize",
    //   "Image must be smaller than 5MB",
    //   (value) => {
    //     // Skip validation if it's an existing image path
    //     if (typeof value === 'string') return true;
    //     return !value || value.size <= 5000000;
    //   }
    // )
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      video: null,
      image: null,
      imagePreview: ""
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    context: { responseVideoUrl },
    onSubmit: async (values) => {
      formik.setTouched({
        title: true,
        description: true,
        video: true,
        image: true
      });

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) return false;

      setButtonLoading(true);
      try {

        let finalImage;
        if (values.image instanceof File) {
          finalImage = await getUploadedVideoLink(values.image, "image");
        }
        // If it's a string (existing image path)
        else if (typeof values.image === 'string') {
          finalImage = values.image;
        }

        const videoToSend = values.video instanceof File ? videoLink : values.video;

        if (!videoToSend) {
          console.error("No video to submit.");
          return;
        }

        const response = await httpRequest(
          `video/update/${id}`,
          "PUT",
          {
            title: values.title,
            description: values.description,
            video: videoToSend,
            image: finalImage
          },
          {},
          true,
          true
        );
        if (response?.code === 200) {

          toast.success("Video updated successfully")
          formik.resetForm()
          navigate("/admin/video-management")
        }
      } catch (error) {
        console.log(error);

      } finally {
        setButtonLoading(false);
      }
    },
  });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;
    const previewUrl = URL.createObjectURL(file);

    formik.setValues({
      ...formik.values,
      image: file,
      imagePreview: previewUrl
    });

    try {
      setLoading(prev => ({ ...prev, image: "pending" }));

      const uploadedImageLink = await getUploadedVideoLink(file, "image");
      setImageLink(uploadedImageLink);

      // Validate if form was previously submitted
      if (formik.submitCount > 0) {
        await formik.validateField("image");
      }

      setLoading(prev => ({ ...prev, image: "success" }));
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Image upload failed. Please try again.");

      setLoading(prev => ({ ...prev, image: "error" }));
    }
  };


  const extractVideoId = useMemo(() => {
    if (!formik.values.video) return null;
    const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = formik.values.video?.match(regex);
    return match ? match[1] : null;
  }, [formik.values.video]);

  // Memoize the video URLs to prevent recreation on every render
  // const videoPreviewUrl = useMemo(() => {
  //   if (!formik.values.video) return null;

  //   if (typeof formik.values.video === 'string') {
  //     return `${process.env.REACT_APP_IMAGE_URL}${formik.values.video}`;
  //   }

  //   if (formik.values.video instanceof File) {
  //     return URL.createObjectURL(formik.values.video);
  //   }

  //   return null;
  // }, [formik.values.video]);

  // Clean up the object URL when component unmounts or video changes
  // useEffect(() => {
  //   return () => {
  //     if (videoPreviewUrl && formik.values.video instanceof File) {
  //       URL.revokeObjectURL(videoPreviewUrl);
  //     }
  //   };
  // }, [videoPreviewUrl, formik.values.video]);

  const fetchVideoEditData = async () => {
    try {
      const response = await httpRequest(
        `video/get/${id}`,
        "GET",
        {},
        {},
        true,
        false
      );
      if (response?.code === 200) {

        console.log("sss", response)
        // const videoUrl = response?.data?.video;
        const imageUrl = response?.data?.image ? `${process.env.REACT_APP_IMAGE_URL}${response?.data?.image}` : "";
        const videoUrl = response?.data?.video ? `${process.env.REACT_APP_IMAGE_URL}${response?.data?.video}` : "";
        setResponseVideoUrl(videoUrl);

        formik.resetForm({
          values: {
            title: response?.data?.title,
            description: response?.data?.description,
            video: response?.data?.video,
            image: response?.data?.image, // or null if you want fresh upload
            imagePreview: response?.data?.image
          }
        });
      }
    } catch (error) {
      console.log("Upload error:", error);
    }
  }

  useEffect(() => {
    if (id) {
      fetchVideoEditData()
    }
  }, [id])

  const getUploadedVideoLink = async (file, key = "test") => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const handleUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress for ${key}: ${percentCompleted}%`);
        setUploadProgress(prev => ({ ...prev, [key]: percentCompleted }));
      };

      const response = await httpRequest(
        "admin/UploadMedia",
        "POST",
        formData,
        {},
        true,
        true,
        "json",
        handleUploadProgress
      );
      return response?.filePath;
    } catch (error) {
      console.log("Upload error:", error);
      throw error;
    }
  };

  const handleVideoChange = async (event) => {
    const file = event.currentTarget.files[0]

    const allowedTypes = ["video/mp4"];

    if (!allowedTypes.includes(file?.type)) {
      toast.error("Unsupported video format. Only MP4 allowed.");
      formik.setFieldValue("video", null);
      return;
    }

    const fileSizeInBytes = file.size;
    const maxFileSize = 2 * 1024 * 1024 * 1024;

    if (fileSizeInBytes > maxFileSize) {
      toast.error("File is too large. Maximum allowed is 2GB.")
      formik.setFieldValue("video", null);
      return;
    }


    if (file) {
      formik.setFieldValue("video", file);
    }

    try {
      // Set loading to pending
      setLoading(prev => ({ ...prev, video: "pending" }));

      const uploadedVideoLink = await getUploadedVideoLink(file, "video");
      console.log("Video uploaded:", uploadedVideoLink);

      setVideoLink(uploadedVideoLink);

      // Set loading to success
      setLoading(prev => ({ ...prev, video: "success" }));
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Video upload failed. Please try again.");

      // Set loading to error
      setLoading(prev => ({ ...prev, video: "error" }));
    }
  }

  return (
    <Row className="mt-4 justify-content-center">
      <Col lg="8">
        <Card className="bg-secondary shadow border-0">
          <CardHeader className="bg-transparent pb-3">
            <div className="text-muted text-center mt-2 mb-3">
              <small>Edit Video</small>
            </div>
          </CardHeader>
          <CardBody className="px-lg-5 py-lg-4">
            <Form onSubmit={formik.handleSubmit}>
              {/* Podcast Title */}
              <FormGroup className="mb-3">
                <Label className="form-control-label">Video Title</Label>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-book-bookmark" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Enter video title"
                    type="text"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting}
                  />
                </InputGroup>
                {formik.touched.title && formik.errors.title && (
                  <div className="text-danger small mt-1">
                    {formik.errors.title}
                  </div>
                )}
              </FormGroup>

              {/* Description */}
              <FormGroup>
                <Label className="form-control-label">Video Description</Label>
                <TextEditor
                  value={formik.values.description}
                  onChange={(html) => formik.setFieldValue("description", html, true)}
                  onBlur={() => formik.setFieldTouched("description", true, true)}
                  placeholder="Write the video description..."
                  hideImageAndLink={false}
                />
                {formik.touched.description && formik.errors.description && (
                  <div className="text-danger small mt-1">
                    {formik.errors.description}
                  </div>
                )}
              </FormGroup>

              <div class="container text-center addflexwrap-video">
                <div class="row">
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-4 addflex-videoimage reducemargin-image">
                      <Label className="form-control-label"> Video</Label>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fab fa-youtube" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Enter video url"
                          type="text"
                          name="video"
                          value={formik.values.video}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={formik.isSubmitting}
                        />
                      </InputGroup>
                      {/* Upload button and status */}
                      {/* <div className="d-flex flex-column gap-2 mb-3 addmrginresponsive-video">
                        <label
                          className={`btn btn-sm btn-primary mb-0 ml-2 addp-addvideo position-relative`}
                          style={{ opacity: loading.video === "pending" ? 0.6 : 1, pointerEvents: loading.video === "pending" ? "none" : "auto" }}
                        >
                          {loading.video !== "pending" && <> <i className="ni ni-cloud-upload-96 mr-1" />
                            {formik.values.video ? "Change Video" : "Upload Video"}</>}
                          <input
                            type="file"
                            accept="video/mp4"
                            onChange={handleVideoChange}
                            style={{ display: 'none' }}
                            disabled={formik.isSubmitting || loading.video === "pending"}
                          />
                          {loading.video === "pending" && (
                            <div className="upload-progress-info mt-1 text-white text-center w-100">
                              <i className="fas fa-spinner fa-spin" /> Uploading: {uploadProgress?.video || 0}%
                            </div>
                          )}
                        </label>

                        {loading.video === "pending" && (
                          <div className="progress-bar-line w-100 mt-1">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${uploadProgress?.video || 0}%` }}
                            />
                          </div>
                        )}
                      </div> */}

                      {/* Video preview */}
                      {/* {videoPreviewUrl && (
                        <div className="mt-2 w-100" >
                          <video
                            controls


                            style={{
                              width: "100%",
                              height: "180px",
                              borderRadius: "6px",
                              objectFit: "cover",
                              display: "block"
                            }}
                            src={videoPreviewUrl}
                          />
                        </div>
                      )} */}

                      {formik.touched.video && formik.errors.video && (
                        <div className="text-danger small mt-1">
                          {formik.errors.video}
                        </div>
                      )}
                    </FormGroup>

                    {extractVideoId && (
                      <div className="mt-3 w-100 addmarginbtm-responsiveimage" >
                        <iframe
                          borderRadius="6px"
                          width="100%"
                          height="180"
                          src={`https://www.youtube.com/embed/${extractVideoId}`}
                          title="YouTube video player"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerpolicy="strict-origin-when-cross-origin"
                          allowfullscreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                  <div class="col-12 col-md-6">
                    <FormGroup className="mb-3 addflex-videoimage reducemargin-image">
                      <Label className="form-control-label" htmlFor="imageUpload">
                        Upload Cover Image
                      </Label>
                      <div className="d-flex flex-column gap-2 mb-3 addmrginresponsive-image">
                        <label
                          className={`btn btn-sm btn-primary mb-0 ml-2 addp-addvideo position-relative`}
                          style={{ opacity: loading.image === "pending" ? 0.6 : 1, pointerEvents: loading.image === "pending" ? "none" : "auto" }}
                        >
                          {loading.image !== "pending" && <><i className="ni ni-cloud-upload-96 mr-1" />
                            {formik.values.image ? "Change Image" : "Upload Image"}</>}
                          <input
                            type="file"
                            name="image"
                            id="imageUpload"
                            accept="image/*"
                            onChange={handleImageChange}
                            onBlur={formik.handleBlur}
                            style={{ display: 'none' }}
                            disabled={formik.isSubmitting || loading.image === "pending"}
                          />
                          {loading.image === "pending" && (
                            <div className="upload-progress-info mt-1 text-white text-center w-100">
                              <i className="fas fa-spinner fa-spin" /> Uploading: {uploadProgress?.image || 0}%
                            </div>
                          )}
                        </label>

                        {loading.image === "pending" && (
                          <div className="progress-bar-line w-100 mt-1">
                            <div
                              className="progress-bar-fill"
                              style={{ width: `${uploadProgress?.image || 0}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {formik.touched.image && formik.errors.image && (
                        <div className="text-danger small mt-1">
                          {formik.errors.image}
                        </div>
                      )}
                    </FormGroup>

                    {/* Image Preview */}
                    {formik.values.imagePreview && (
                      <div className="mt-3 addmarginbtm-responsiveimage">
                        <img
                          src={
                            typeof formik.values.imagePreview === 'string' &&
                              formik.values.imagePreview.startsWith('blob:')
                              ? formik.values.imagePreview // Use blob URL directly
                              : `${process.env.REACT_APP_IMAGE_URL}${formik.values.imagePreview}` // Prepend base URL for existing images
                          }
                          alt="Preview"
                          className="addwh-image"
                          style={{
                            width: "100%",
                            height: "180px",
                            borderRadius: "6px",
                            objectFit: "cover",
                            display: "block"
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center m-2 d-flex align-items-center justify-content-center removemtop-edit">
                <Button
                  disabled={
                    buttonLoading || Object.values(loading).some(value => (value === "pending" || value === "error"))
                  }
                  className="my-4 d-flex align-items-center justify-content-center"
                  color="primary"
                  type="submit"
                >
                  {buttonLoading ? <span className="button-loader"></span> : "Save Video"}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default EditVideo;